import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import {
  IDepartment,
  IDistrict,
  ILocation,
  ILookup,
  IMunicipality,
  ISector,
  IUser,
} from "../../core/_models";

import CustomFormSearchSelect from "./CustomFormSearchSelect";
import CustomFormInput from "./CustomFormInput";
import { createNewProject } from "../../project/core/_request";
import { useAuth } from "../../auth";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { getUsers } from "../../project/core/_user";
import { ProjectFormData } from "../../project";
import { getApiErrorMessage } from "../../../../utils/utils";

type Props = {
  show: boolean;
  onClose: () => void;
  onCreateProject: () => void;
  districts: IDistrict[];
  departments: IDepartment[];
  sectors: ISector[];
  priorities: ILookup[];
  handleFetchMunicipalityData?: (district_id: number) => void;
  municipalityData: IMunicipality[];
};

const initialForm: ProjectFormData = {
  name: "",
  short_name: "",
  desc: "",
  sector_id: "",
  priority_id: "",
  // location_id: "",
  district_id: "",
  municipality_id: "",
  department_id: "",
  project_owner: "",
  project_manager: "",
  project_lead: "",
};

const FIELD_MAX_LENGTHS: Partial<Record<keyof ProjectFormData, number>> = {
  name: 250,
  short_name: 50,
  desc: 150,
};

const FIELD_MAX_LENGTH_LABELS: Partial<Record<keyof ProjectFormData, string>> = {
  name: "Project name",
  short_name: "Short name",
  desc: "Description",
};

const getMaxLengthMessage = (field: keyof ProjectFormData): string | undefined => {
  const maxLength = FIELD_MAX_LENGTHS[field];
  if (!maxLength) {
    return undefined;
  }

  const label = FIELD_MAX_LENGTH_LABELS[field] ?? field;
  return `${label} must be ${maxLength} characters or less`;
};

const CreateProjectModal: React.FC<Props> = ({
  show,
  onClose,
  onCreateProject,
  districts,
  departments,
  sectors,
  priorities,
  handleFetchMunicipalityData,
  municipalityData
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [popupLoader, setPopupLoader] = useState(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [form, setForm] = useState<ProjectFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const hasFetched = useRef<boolean>(false);
  const { auth } = useAuth();
  const { showSnackbar } = useSnackbar();

  // Utility to remove repetitive map code
  const mapOptions = useCallback(
    (list: any[], labelKey: string, valueKey: string, emailKey?: string) =>
      list.map((item) => ({
        label: emailKey ? item[labelKey] + " (" + item[emailKey] + ")" : item[labelKey],
        value: item[valueKey],
      })),
    []
  );

  // Clear error on change
  const resetError = useCallback((name: string) => {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  }, []);

  const setMaxLengthError = useCallback((name: keyof ProjectFormData, exceededLimit: boolean) => {
    if (!exceededLimit) {
      resetError(name);
      return;
    }

    const message = getMaxLengthMessage(name);
    if (!message) {
      resetError(name);
      return;
    }

    setErrors((prev) => {
      if (prev[name] === message) return prev;
      return { ...prev, [name]: message };
    });
  }, [resetError]);

  // Input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const field = name as keyof ProjectFormData;
      const maxLength = FIELD_MAX_LENGTHS[field];
      const exceededLimit = Boolean(maxLength && value.length > maxLength);

      setForm((prev) => ({
        ...prev,
        [name]: maxLength ? value.slice(0, maxLength) : value,
      }));
      setMaxLengthError(field, exceededLimit);
    },
    [setMaxLengthError]
  );
  // Short name change (uppercase)
  const handleShortNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let { name, value } = e.target;
      const maxLength = FIELD_MAX_LENGTHS.short_name ?? 50;
      value = value.replace(/\s+/g, "");   // remove spaces
      value = value.toUpperCase();         // uppercase
      value = value.replace(/[^A-Z0-9]/g, ""); // remove special characters
      const exceededLimit = value.length > maxLength;
      value = value.slice(0, maxLength);
      setForm((prev) => ({ ...prev, [name]: value }));
      setMaxLengthError(name as keyof ProjectFormData, exceededLimit);
    },
    [setMaxLengthError]
  );

  // Select change
  const handleSelectChange = useCallback(
    (name: keyof ProjectFormData, value: string | number) => {
      setForm((prev) => ({ ...prev, [name]: value }));
      resetError(name);
    },
    [resetError]
  );

  // Validation logic (clean & reusable)
  const validate = useCallback(() => {
    const messages: Record<string, string> = {
      name: "Project name is required",
      short_name: "Short name is required",
      desc: "Description is required",
      priority_id: "Timeline Priority is required",
      // location_id: "Location is required",
      district_id: "District Municipality is required",
      department_id: "Department is required",
      sector_id: "Sector is required",
      project_owner: "Project owner is required",
    };

    const newErrors: Record<string, string> = {};

    Object.entries(messages).forEach(([field, msg]) => {
      const value = form[field as keyof ProjectFormData];
      if (!value) {
        newErrors[field] = msg;
        return;
      }

      const maxLength = FIELD_MAX_LENGTHS[field as keyof ProjectFormData];
      const maxLengthMessage = getMaxLengthMessage(field as keyof ProjectFormData);
      if (maxLength && maxLengthMessage && String(value).length > maxLength) {
        newErrors[field] = maxLengthMessage;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  // Handle Submit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      setPopupLoader(true);
      try {
        const resp = await createNewProject({
          ...form,
        });

        if (resp.status === "ok") {
          showSnackbar(resp.message, "success");
          onClose();
          onCreateProject();
        } else {
          showSnackbar(resp.message, "danger");
        }
      } catch (err: unknown) {
        showSnackbar(getApiErrorMessage(err, "Failed to create project"), "danger");
      } finally {
        setPopupLoader(false);
      }
    },
    [validate, form, auth, onClose, onCreateProject, showSnackbar]
  );

  // Fetch dropdown data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usr] = await Promise.all([
        getUsers(),
      ]);
      if (usr.status === "ok") setUsers(usr.data.list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch only once
  useEffect(() => {
    if (show && !hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [show, fetchData]);

  // Reset on modal close
  useEffect(() => {
    if (!show) {
      setForm(initialForm);
      setErrors({});
    }
  }, [show]);

  const handleDistrictChange = useCallback(
    (district_id: string | number) => {
      handleSelectChange("district_id", district_id);
      handleSelectChange("municipality_id", "");
      if (district_id && typeof district_id === "number" && handleFetchMunicipalityData) {
        handleFetchMunicipalityData(district_id);
      }
    },
    [handleSelectChange]
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="modal-dialog modal-lg modal-dialog-centered"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Create Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: "65vh" }}>
              <Row>
                <Col md={6}>
                  {/* Project Name */}
                  <Form.Group className="mb-3">
                    <Form.Label className="required">Project Name</Form.Label>
                    <CustomFormInput
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Enter project name"
                    />
                    {errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">
                      Project Short Name
                    </Form.Label>
                    <CustomFormInput
                      name="short_name"
                      value={form.short_name}
                      onChange={handleShortNameChange}
                      placeholder="Enter project short name"
                    />
                    {errors.short_name && (
                      <small className="text-danger">
                        {errors.short_name}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Description */}
              <Form.Group className="mb-3">
                <Form.Label className="required">Description</Form.Label>
                <CustomFormInput
                  name="desc"
                  value={form.desc}
                  onChange={handleChange}
                  placeholder="Enter description"
                  as="textarea"
                  rows={3}
                />
                {errors.desc && (
                  <small className="text-danger">{errors.desc}</small>
                )}
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">Timeline Priority</Form.Label>
                    <CustomFormSearchSelect
                      name="priority_id"
                      value={form.priority_id}
                      onChange={(v) =>
                        handleSelectChange("priority_id", v as string | number)
                      }
                      options={mapOptions(
                        priorities,
                        "display_name",
                        "data_key"
                      )}
                      placeholder="Select Priority"
                      usePortal
                    />
                    {errors.priority_id && (
                      <small className="text-danger">
                        {errors.priority_id}
                      </small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">WCIS Sector</Form.Label>
                    <CustomFormSearchSelect
                      name="sector_id"
                      value={form.sector_id}
                      onChange={(v) =>
                        handleSelectChange("sector_id", v as string | number)
                      }
                      options={mapOptions(
                        sectors,
                        "name",
                        "sector_id"
                      )}
                      placeholder="Select Sector"
                      usePortal
                    />
                    {errors.sector_id && (
                      <small className="text-danger">
                        {errors.sector_id}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              {/* Department, Location, Owner, Manager */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">Department</Form.Label>
                    <CustomFormSearchSelect
                      name="department_id"
                      value={form.department_id}
                      onChange={(v) =>
                        handleSelectChange("department_id", v as string | number)
                      }
                      options={mapOptions(
                        departments,
                        "department_name",
                        "department_id"
                      )}
                      placeholder="Select Department"
                      usePortal
                    />
                    {errors.department_id && (
                      <small className="text-danger">
                        {errors.department_id}
                      </small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} />
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">District Municipality</Form.Label>
                    <CustomFormSearchSelect
                      name="district_id"
                      value={form.district_id}
                      onChange={(v) => handleDistrictChange(v as string | number)}
                      options={mapOptions(
                        districts,
                        "district_name",
                        "district_id"
                      )}
                      placeholder="Select District Municipality"
                      usePortal
                    />
                    {errors.district_id && (
                      <small className="text-danger">
                        {errors.district_id}
                      </small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="">Local Municipality</Form.Label>
                    <CustomFormSearchSelect
                      name="municipality_id"
                      value={form.municipality_id}
                      onChange={(v) => handleSelectChange("municipality_id", v as string | number)}
                      options={mapOptions(
                        municipalityData,
                        "name",
                        "municipality_id"
                      )}
                      placeholder="Select Local Municipality"
                      usePortal
                    />
                  </Form.Group>
                </Col>

                {/* Owner */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="required">Project Owner</Form.Label>
                    <CustomFormSearchSelect
                      name="project_owner"
                      value={form.project_owner}
                      onChange={(v) =>
                        handleSelectChange("project_owner", v as string | number)
                      }
                      options={mapOptions(users, "fullname", "user_id", "email")}
                      placeholder="Select Owner"
                      usePortal
                    />
                    {errors.project_owner && (
                      <small className="text-danger">
                        {errors.project_owner}
                      </small>
                    )}
                  </Form.Group>
                </Col>

                {/* Manager */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label >Project Manager</Form.Label>
                    <CustomFormSearchSelect
                      name="project_manager"
                      value={form.project_manager}
                      onChange={(v) =>
                        handleSelectChange("project_manager", v as string | number)
                      }
                      options={mapOptions(users, "fullname", "user_id", "email",)}
                      placeholder="Select Manager"
                      usePortal
                    />
                  </Form.Group>
                </Col>

                {/* Lead */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label >Project Lead</Form.Label>
                    <CustomFormSearchSelect
                      name="project_lead"
                      value={form.project_lead}
                      onChange={(v) =>
                        handleSelectChange("project_lead", v as string | number)
                      }
                      options={mapOptions(users, "fullname", "user_id", "email",)}
                      placeholder="Select Lead"
                      usePortal
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={popupLoader}
          >
            {popupLoader ? <Spinner size="sm" /> : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateProjectModal;